import React from "react";
import QRCode from "react-qr-code";
import TenantCrest from "./TenantCrest";
import {
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LineChart, Line, Legend,
} from "recharts";
import {
  Award, TrendingUp, TrendingDown, Minus, Star, ShieldAlert,
  BookOpen, Users, Trophy, Heart, GraduationCap, Sparkles,
  CheckCircle2, AlertTriangle, Calendar, MapPin, Mail, Phone,
} from "lucide-react";

// Brand-integrity boundary (2026-09-25 executive directive):
// No third-party stock imagery may be used as a default avatar. When a
// student has no authorised photograph, we render an institution-neutral
// SVG monogram derived from their initials — not a Pexels/Unsplash face.
const AVATAR_FALLBACK = null;

function initialsMonogramDataUri(fullName) {
  const initials = (fullName || "")
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "").join("") || "·";
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240' viewBox='0 0 240 240'>` +
    `<defs><radialGradient id='g' cx='30%' cy='24%' r='75%'>` +
    `<stop offset='0%' stop-color='#0A3466'/>` +
    `<stop offset='100%' stop-color='#00152F'/>` +
    `</radialGradient></defs>` +
    `<rect width='240' height='240' fill='url(#g)'/>` +
    `<circle cx='120' cy='120' r='96' fill='none' stroke='#D9A24A' stroke-opacity='0.35' stroke-width='1'/>` +
    `<text x='120' y='140' text-anchor='middle' font-family='Georgia, serif' font-size='84' font-weight='800' fill='#D9A24A' letter-spacing='2'>${initials}</text>` +
    `</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

function TrendIcon({ trend }) {
  if (trend === "up") return <TrendingUp className="w-4 h-4 text-emerald-600" />;
  if (trend === "down") return <TrendingDown className="w-4 h-4 text-rose-600" />;
  return <Minus className="w-4 h-4 text-slate-400" />;
}

function Section({ title, kicker, children, right }) {
  return (
    <section className="edu360-section mb-10">
      <div className="flex items-end justify-between border-b-2 border-[color:var(--hl-navy)] pb-3 mb-6">
        <div>
          {kicker && <div className="edu360-kicker">{kicker}</div>}
          <h2 className="font-display font-bold text-2xl tracking-tight text-[color:var(--hl-navy)]">
            {title}
          </h2>
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function StatMini({ label, value, hint, tone }) {
  const tones = {
    accent: "text-[color:var(--hl-orange)]",
    positive: "text-emerald-600",
    warning: "text-amber-600",
    danger: "text-rose-600",
    default: "text-[color:var(--hl-navy)]",
  };
  return (
    <div className="edu360-stat-mini">
      <div className="edu360-kicker">{label}</div>
      <div className={`font-display font-bold text-2xl tracking-tight ${tones[tone] || tones.default}`}>{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, tone }) {
  return (
    <div className={`edu360-kpi ${tone === "dark" ? "edu360-kpi-dark" : ""}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="edu360-kicker">{label}</div>
        {Icon && <Icon className={`w-4 h-4 ${tone === "dark" ? "text-white/70" : "text-[color:var(--hl-orange)]"}`} />}
      </div>
      <div className={`font-display font-bold text-3xl tracking-tight ${tone === "dark" ? "text-white" : "text-[color:var(--hl-navy)]"}`}>
        {value}
      </div>
      {sub && <div className={`text-xs mt-1 ${tone === "dark" ? "text-white/70" : "text-slate-500"}`}>{sub}</div>}
    </div>
  );
}

function SubjectCard({ s }) {
  const bar = Math.min(100, Math.max(0, s.average));
  const classBar = Math.min(100, Math.max(0, s.class_average));
  return (
    <div className="edu360-subject-card" data-testid={`subject-card-${s.subject_id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="edu360-kicker">{s.department}</div>
          <div className="font-display font-bold text-lg tracking-tight text-[color:var(--hl-navy)] leading-tight">
            {s.subject}
          </div>
          <div className="text-xs text-slate-500 mt-1">{s.teacher_name}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="edu360-grade">{s.grade_letter}</div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">Grade</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-3">
        <StatMini label="Average" value={`${s.average}%`} tone={s.strength ? "positive" : s.risk ? "danger" : "default"} />
        <StatMini label="Class avg" value={`${s.class_average}%`} hint="cohort" />
        <StatMini
          label="Position"
          value={s.position ? `${s.position}/${s.cohort}` : "—"}
          tone={s.position && s.position <= 3 ? "accent" : "default"}
        />
      </div>

      <div className="edu360-progress-wrapper mb-3">
        <div className="flex justify-between text-[10px] uppercase tracking-widest text-slate-500 mb-1">
          <span>Student</span>
          <span className="text-slate-400">Class</span>
        </div>
        <div className="edu360-progress-track">
          <div
            className="edu360-progress-fill"
            style={{ width: `${bar}%` }}
          />
          <div className="edu360-progress-marker" style={{ left: `${classBar}%` }} title={`Class avg ${classBar}%`} />
        </div>
      </div>

      <div className="flex items-center gap-2 text-xs mb-3">
        <TrendIcon trend={s.trend} />
        <span className="text-slate-600">
          {s.trend === "up" ? "Improving" : s.trend === "down" ? "Regressing" : "Steady"} · {s.assessments} assessments
        </span>
        {s.strength && <span className="edu360-tag-strength"><Star className="w-3 h-3" /> Strength</span>}
        {s.risk && <span className="edu360-tag-risk"><ShieldAlert className="w-3 h-3" /> Watch</span>}
      </div>

      <div className="text-[13px] italic text-slate-700 border-l-2 border-[color:var(--hl-orange)] pl-3 leading-relaxed">
        “{s.teacher_comment}”
      </div>
    </div>
  );
}

function BehaviourBars({ matrix }) {
  return (
    <div className="space-y-2.5">
      {matrix.map((m) => {
        const isAssessed = m.score !== null && m.score !== undefined;
        return (
          <div key={m.label} className="flex items-center gap-3" data-testid={`wsi-row-${m.label.toLowerCase()}`}>
            <div className="w-28 text-xs text-slate-600 font-medium">{m.label}</div>
            <div className="flex-1 edu360-progress-track">
              <div
                className="edu360-progress-fill"
                style={{
                  width: isAssessed ? `${m.score}%` : "0%",
                  background: isAssessed ? undefined : "#e2e8f0",
                }}
              />
            </div>
            <div className={`text-xs text-right mono ${isAssessed ? "w-10 font-semibold text-slate-700" : "w-32 italic text-slate-400"}`}>
              {isAssessed ? m.score : "Not Yet Assessed"}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function IndicatorCard({ icon, label, value, sub, tone }) {
  const isAssessed = value !== null && value !== undefined && value !== "null/100" && value !== "null%" && !String(value).includes("null");
  const displayValue = isAssessed ? value : "Not Yet Assessed";
  const displaySub = isAssessed ? sub : "pending canonical rule";
  return (
    <KpiCard
      icon={icon}
      label={label}
      value={displayValue}
      sub={displaySub}
      tone={tone}
    />
  );
}

function AINarrative({ text }) {
  // Split by numbered sections
  const parts = (text || "").split(/\n(?=\d\.\s|\*\*)/g).filter(Boolean);
  return (
    <div className="space-y-3">
      {parts.length === 0 && (
        <p className="text-sm text-slate-600 italic">{text}</p>
      )}
      {parts.map((p, i) => (
        <p key={i} className="text-[13px] text-slate-700 leading-relaxed whitespace-pre-wrap">
          {p.trim()}
        </p>
      ))}
    </div>
  );
}

export default function PremiumReport({ data }) {
  const s = data.student;
  const school = data.school;
  const k = data.kpis;
  const radar = data.subjects.map((x) => ({ subject: x.subject.length > 10 ? x.subject.slice(0, 10) + "…" : x.subject, score: x.average, fullMark: 100 }));
  const attTrend = data.attendance.trend;
  const verificationURL = `${window.location.origin}${window.location.pathname}#${data.meta.verification_id}`;
  // Brand-integrity: never render third-party stock imagery as a default
  // avatar. If the student has no authorised photo (or its URL comes from a
  // known third-party stock source), fall back to an initials monogram.
  const FORBIDDEN_HOSTS = /(?:images\.pexels\.com|images\.unsplash\.com)/i;
  const authorisedPhoto = s.photo_url && !FORBIDDEN_HOSTS.test(s.photo_url) ? s.photo_url : null;
  const monogram = initialsMonogramDataUri(`${s.first_name || ""} ${s.last_name || ""}`);
  const photo = authorisedPhoto || monogram;

  return (
    <div className="edu360-report" data-testid="premium-report">
      {/* ============ COVER ============ */}
      <div className="edu360-report-page edu360-report-cover" data-testid="report-cover">
        {/*
          Brand-integrity boundary (2026-09-25):
          The cover renders an institution-neutral Education360 navy/gold
          environment. `school.cover_image_url` is deliberately NOT consumed
          here even if a legacy value still exists on a tenant record.
        */}
        <div
          className="edu360-cover-bg"
          style={{
            background:
              "radial-gradient(70% 46% at 18% 14%, #0A3466 0%, #002147 42%, #00152F 100%)",
          }}
        />
        <div className="edu360-cover-veil" />
        <div className="edu360-cover-inner">
          <div className="edu360-cover-topbar">
            <div className="flex items-center gap-3">
              <TenantCrest tenant={school} size="lg" testId="report-crest-cover" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.32em] text-white/70">Established Excellence</div>
                <div className="font-display font-bold text-xl leading-tight">{school.name}</div>
                <div className="text-xs text-white/70 italic mt-0.5">{school.motto}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.28em] text-white/60">Report Reference</div>
              <div className="mono text-sm text-white/90 mt-0.5">{data.meta.verification_id}</div>
            </div>
          </div>

          <div className="edu360-cover-middle">
            <div className="edu360-portrait-wrap">
              <img
                src={photo}
                alt={`${s.first_name} ${s.last_name}`}
                className="edu360-portrait"
                onError={(e) => { e.currentTarget.src = monogram; }}
              />
              <div className="edu360-portrait-badge">{s.house}</div>
            </div>
            <div className="edu360-cover-title-block">
              <div className="text-[11px] uppercase tracking-[0.4em] text-white/70">Student Academic Report</div>
              <h1 className="font-display font-black text-white tracking-tight leading-[1.05] text-5xl mt-3">
                {s.first_name} {s.last_name}
              </h1>
              <div className="mt-4 text-white/80 text-sm">
                {s.year_group} · {data.class?.name || ""} · {s.house} House · {s.boarding ? "Boarder" : "Day Scholar"}
              </div>
              <div className="edu360-cover-year">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.32em] text-white/60">Academic Year</div>
                  <div className="font-display font-bold text-2xl">{data.meta.academic_year}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.32em] text-white/60">Term</div>
                  <div className="font-display font-bold text-2xl">{data.meta.term}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.32em] text-white/60">Issued</div>
                  <div className="font-display font-bold text-2xl">{data.meta.approval_date || data.meta.generated_at?.slice(0, 10)}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="edu360-cover-bottom">
            <div className="flex items-end justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.32em] text-white/60 mb-1">Prepared by</div>
                <div className="font-display font-bold text-lg">Education360™ · School360</div>
                <div className="text-[11px] text-white/60 mt-0.5">A THOM Digital Infrastructure platform</div>
              </div>
              <div className="edu360-qr-block">
                <div className="bg-white p-2 rounded">
                  <QRCode value={verificationURL} size={72} />
                </div>
                <div className="text-[9px] uppercase tracking-[0.24em] text-white/60 mt-1 text-center">Verify</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============ PROFILE SUMMARY ============ */}
      <div className="edu360-report-page">
        <Section title="Student Profile" kicker="Section 01">
          <div className="grid md:grid-cols-12 gap-6 items-start">
            <div className="md:col-span-4">
              <div className="edu360-profile-card">
                <img
                  src={photo}
                  alt={`${s.first_name} ${s.last_name}`}
                  className="edu360-profile-photo"
                  onError={(e) => { e.currentTarget.src = AVATAR_FALLBACK; }}
                />
                <div className="edu360-profile-name">
                  <div className="font-display font-bold text-2xl tracking-tight text-[color:var(--hl-navy)]">
                    {s.first_name} {s.last_name}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Admission {s.admission_no}</div>
                </div>
              </div>
            </div>
            <div className="md:col-span-8 grid grid-cols-2 gap-x-6 gap-y-4">
              <ProfileRow label="Current Grade" value={s.year_group} />
              <ProfileRow label="Class" value={data.class?.name || "—"} />
              <ProfileRow label="House" value={`${s.house} House`} />
              <ProfileRow label="Boarding" value={s.boarding ? "Boarder" : "Day Scholar"} />
              <ProfileRow label="Age" value={ageFrom(s.date_of_birth)} />
              <ProfileRow label="Date of Birth" value={s.date_of_birth} />
              <ProfileRow label="Gender" value={s.gender} />
              <ProfileRow label="Nationality" value={s.nationality || "—"} />
              <ProfileRow label="Scholarship" value={s.scholarship || "None"} />
              <ProfileRow label="Class Teacher" value={data.class?.homeroom_teacher_id ? "Assigned" : "—"} />
              <ProfileRow label="Overall Average" value={`${k.overall_average}%`} accent />
              <ProfileRow label="Promotion Status" value={k.overall_average >= 50 ? "On Track" : "Under Review"} />
            </div>
          </div>
        </Section>

        <Section title="Executive Academic Dashboard" kicker="Section 02">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard icon={Award} tone="dark" label="Overall Average" value={`${k.overall_average}%`} sub="term-to-date" />
            <KpiCard icon={Calendar} label="Attendance" value={`${k.attendance_pct}%`} sub={k.attendance_pct >= 95 ? "Exemplary" : "On target"} />
            <KpiCard icon={Heart} label="Behaviour" value={`${k.behaviour_score}/100`} sub="composite score" />
            <IndicatorCard icon={CheckCircle2} tone="dark" label="Homework" value={k.homework_completion != null ? `${k.homework_completion}%` : null} sub="completion" />
            <IndicatorCard icon={BookOpen} label="Assignments" value={k.assignment_completion != null ? `${k.assignment_completion}%` : null} sub="completion" />
            <IndicatorCard icon={Users} label="Participation" value={k.participation != null ? `${k.participation}/100` : null} sub="class engagement" />
            <IndicatorCard icon={Trophy} label="Leadership" value={k.leadership != null ? `${k.leadership}/100` : null} sub="peer signal" />
            <IndicatorCard icon={GraduationCap} tone="dark" label="Academic Growth" value={k.academic_growth != null ? `${k.academic_growth}` : null} sub="growth index" />
            <IndicatorCard icon={Trophy} label="Sports Participation" value={k.sports_participation != null ? `${k.sports_participation}%` : null} />
            <IndicatorCard icon={BookOpen} label="Library Activity" value={k.library_activity != null ? `${k.library_activity}/100` : null} />
            <KpiCard icon={Heart} label="Medical Visits" value={k.medical_visits} sub="this term" />
            <IndicatorCard icon={AlertTriangle} label="AI Risk Score" value={k.ai_risk_score != null ? `${k.ai_risk_score}` : null} sub={k.ai_risk_score != null && k.ai_risk_score > 40 ? "monitor" : "low"} />
          </div>
        </Section>
      </div>

      {/* ============ SUBJECT PERFORMANCE ============ */}
      <div className="edu360-report-page">
        <Section title="Subject Performance" kicker="Section 03" right={
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[color:var(--hl-orange)]" /> Student</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-400" /> Class average</span>
          </div>
        }>
          <div className="grid md:grid-cols-2 gap-4">
            {data.subjects.map((s) => <SubjectCard key={s.subject_id} s={s} />)}
          </div>
        </Section>
      </div>

      {/* ============ INFOGRAPHICS ============ */}
      <div className="edu360-report-page">
        <Section title="Performance Infographics" kicker="Section 04">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="edu360-chart-card">
              <div className="edu360-kicker mb-2">Competency Radar</div>
              <div className="h-72">
                <ResponsiveContainer>
                  <RadarChart data={radar} outerRadius="75%">
                    <PolarGrid stroke="#e2e8f0" />
                    <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: "#475569" }} />
                    <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 9, fill: "#94a3b8" }} />
                    <Radar name="Student" dataKey="score" stroke="#D9531E" fill="#D9531E" fillOpacity={0.35} strokeWidth={2} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="edu360-chart-card">
              <div className="edu360-kicker mb-2">Attendance Trend</div>
              <div className="h-72">
                <ResponsiveContainer>
                  <LineChart data={attTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" fontSize={10} stroke="#94a3b8" />
                    <YAxis domain={[70, 100]} fontSize={10} stroke="#94a3b8" />
                    <Tooltip contentStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="attendance_pct" stroke="#002147" strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="edu360-chart-card">
              <div className="edu360-kicker mb-2">Subject vs Class Average</div>
              <div className="h-72">
                <ResponsiveContainer>
                  <BarChart data={data.subjects.map((s) => ({ name: s.subject.split(" ")[0], student: s.average, cohort: s.class_average }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="name" fontSize={10} stroke="#94a3b8" />
                    <YAxis fontSize={10} stroke="#94a3b8" />
                    <Tooltip contentStyle={{ fontSize: 11 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="student" fill="#D9531E" radius={[3, 3, 0, 0]} name="Student" />
                    <Bar dataKey="cohort" fill="#94a3b8" radius={[3, 3, 0, 0]} name="Class" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="edu360-chart-card">
              <div className="edu360-kicker mb-2">Behaviour Matrix</div>
              <div className="pt-2">
                <BehaviourBars matrix={data.behaviour.matrix} />
              </div>
            </div>
          </div>
        </Section>
      </div>

      {/* ============ AI EXECUTIVE SUMMARY ============ */}
      <div className="edu360-report-page">
        <Section title="AI Executive Summary" kicker="Section 05" right={
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[color:var(--hl-orange)] font-semibold">
            <Sparkles className="w-3.5 h-3.5" /> AI-Generated Narrative
          </div>
        }>
          <div className="edu360-ai-panel">
            <AINarrative text={data.ai_narrative} />
            <div className="mt-4 text-[10px] uppercase tracking-widest text-slate-500">
              This narrative is synthesised by Education360 AI (GPT-5.2) from aggregated, tenant-scoped academic data.
              It is provided as a professional summary and does not replace the professional judgement of the school.
            </div>
          </div>
        </Section>

        <Section title="Teacher Commentary" kicker="Section 06">
          <div className="grid md:grid-cols-2 gap-4">
            {data.teacher_comments.slice(0, 8).map((c, i) => (
              <div key={i} className="edu360-teacher-panel">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="edu360-kicker">{c.subject}</div>
                    <div className="font-display font-bold text-sm tracking-tight text-[color:var(--hl-navy)]">{c.teacher}</div>
                  </div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-widest">{c.date}</div>
                </div>
                <p className="text-[13px] text-slate-700 italic leading-relaxed">“{c.comment}”</p>
                <div className="mt-4 border-t border-slate-100 pt-2 flex items-center justify-between text-[10px] uppercase tracking-widest text-slate-400">
                  <span>Signature</span>
                  <span className="font-display italic text-[color:var(--hl-navy)] normal-case tracking-normal">— {c.teacher}</span>
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* ============ ATTENDANCE + BEHAVIOUR DETAIL ============ */}
      <div className="edu360-report-page">
        <Section title="Attendance Intelligence" kicker="Section 07">
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            <KpiCard tone="dark" label="Attendance" value={`${data.attendance.pct}%`} sub={`${data.attendance.total} days`} />
            <KpiCard label="Present" value={data.attendance.present} sub="on time" />
            <KpiCard label="Late" value={data.attendance.late} sub="tardies" />
            <KpiCard label="Absent" value={data.attendance.absent} sub={`${data.attendance.excused} excused`} />
          </div>
          <div className="edu360-chart-card">
            <div className="edu360-kicker mb-2">Monthly attendance</div>
            <div className="h-56">
              <ResponsiveContainer>
                <BarChart data={attTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" fontSize={10} stroke="#94a3b8" />
                  <YAxis fontSize={10} stroke="#94a3b8" />
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                  <Bar dataKey="present" stackId="a" fill="#059669" name="Present" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="late" stackId="a" fill="#D9531E" name="Late" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="absent" stackId="a" fill="#dc2626" name="Absent" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Section>

        <Section title="Behaviour Profile" kicker="Section 08">
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <KpiCard tone="dark" icon={Award} label="Overall Behaviour" value={`${data.behaviour.score}`} sub="composite" />
            <KpiCard icon={Star} label="Commendations" value={data.behaviour.positive} sub="positive incidents" />
            <KpiCard icon={ShieldAlert} label="Concerns" value={data.behaviour.minor + data.behaviour.major} sub={`${data.behaviour.minor} minor · ${data.behaviour.major} major`} />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="edu360-chart-card">
              <div className="edu360-kicker mb-4">Character matrix</div>
              <BehaviourBars matrix={data.behaviour.matrix} />
            </div>
            <div className="edu360-chart-card">
              <div className="edu360-kicker mb-3">Recent incidents</div>
              <div className="space-y-3">
                {data.behaviour.incidents.slice(0, 5).map((i, idx) => (
                  <div key={idx} className="flex items-start gap-3 text-xs">
                    <div className={`w-1.5 h-1.5 rounded-full mt-1.5 ${i.category === "positive" ? "bg-emerald-500" : i.category === "minor" ? "bg-amber-500" : "bg-rose-500"}`} />
                    <div className="flex-1">
                      <div className="text-slate-700 font-medium">{i.description}</div>
                      <div className="text-slate-400 mt-0.5">{i.date} · {i.action_taken}</div>
                    </div>
                  </div>
                ))}
                {data.behaviour.incidents.length === 0 && <div className="text-xs text-slate-400 italic">No recorded incidents this term.</div>}
              </div>
            </div>
          </div>
        </Section>
      </div>

      {/* ============ EXTRACURRICULARS + PARENT/MEDICAL/FEES ============ */}
      <div className="edu360-report-page">
        <Section title="Extracurricular & Enrichment" kicker="Section 09">
          {data.extracurricular.length === 0 && (
            <div className="text-sm text-slate-500 italic">No enrichment activities recorded this term.</div>
          )}
          <div className="grid md:grid-cols-3 gap-4">
            {data.extracurricular.map((x, i) => (
              <div key={i} className="edu360-activity-card">
                <div className="flex items-center gap-2 mb-2">
                  <Trophy className="w-4 h-4 text-[color:var(--hl-orange)]" />
                  <div className="edu360-kicker">{x.category}</div>
                </div>
                <div className="font-display font-bold text-base tracking-tight text-[color:var(--hl-navy)]">{x.name}</div>
                {x.schedule && <div className="text-xs text-slate-500 mt-1">Meets · {x.schedule}</div>}
              </div>
            ))}
          </div>
        </Section>

        <Section title="Parent & Guardian Information" kicker="Section 10">
          <div className="grid md:grid-cols-2 gap-4">
            {data.parents.map((p, i) => (
              <div key={i} className="edu360-teacher-panel">
                <div className="edu360-kicker">{p.relationship}</div>
                <div className="font-display font-bold text-lg tracking-tight text-[color:var(--hl-navy)] mt-1">{p.name}</div>
                <div className="mt-2 space-y-1 text-xs text-slate-600">
                  <div className="flex items-center gap-2"><Mail className="w-3 h-3 text-slate-400" /> {p.email}</div>
                  <div className="flex items-center gap-2"><Phone className="w-3 h-3 text-slate-400" /> {p.phone}</div>
                </div>
              </div>
            ))}
            <div className="edu360-teacher-panel">
              <div className="edu360-kicker">Fee Summary · Autumn</div>
              <div className="mt-2 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Billed</span><span className="mono font-semibold">${data.finance.billed.toLocaleString()}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Paid</span><span className="mono font-semibold">${data.finance.paid.toLocaleString()}</span></div>
                <div className="flex justify-between border-t border-slate-100 pt-1 mt-1"><span className="text-slate-500">Outstanding</span><span className={`mono font-bold ${data.finance.outstanding > 0 ? "text-rose-600" : "text-emerald-600"}`}>${data.finance.outstanding.toLocaleString()}</span></div>
              </div>
            </div>
          </div>
        </Section>

        <Section title="Health & Medical Summary" kicker="Section 11">
          {data.medical.length === 0 ? (
            <div className="text-sm text-slate-500 italic">No medical events recorded this term.</div>
          ) : (
            <div className="space-y-2">
              {data.medical.map((m, i) => (
                <div key={i} className="edu360-teacher-panel flex items-start gap-3">
                  <Heart className="w-4 h-4 text-[color:var(--hl-orange)] mt-0.5" />
                  <div className="flex-1">
                    <div className="text-xs text-slate-500">{m.date} · {m.type}</div>
                    <div className="text-sm text-slate-700 mt-0.5">{m.description}</div>
                    {m.treatment && <div className="text-xs text-slate-500 mt-1 italic">Treatment: {m.treatment}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
          {s.medical_notes && (
            <div className="mt-4 edu360-ai-panel">
              <div className="edu360-kicker mb-1">Recorded Medical Notes</div>
              <div className="text-sm text-slate-700">{s.medical_notes}</div>
            </div>
          )}
        </Section>
      </div>

      {/* ============ PRINCIPAL + RECOMMENDATIONS ============ */}
      <div className="edu360-report-page">
        <Section title="Executive Recommendations" kicker="Section 12">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="edu360-teacher-panel">
              <div className="edu360-kicker mb-1">Immediate Actions</div>
              <ul className="text-sm text-slate-700 list-disc list-inside space-y-1 mt-2">
                <li>Schedule a Mathematics enrichment slot for Wednesday prep.</li>
                <li>Confirm Chemistry practical support during lunchtime lab.</li>
              </ul>
            </div>
            <div className="edu360-teacher-panel">
              <div className="edu360-kicker mb-1">Short-Term (Term)</div>
              <ul className="text-sm text-slate-700 list-disc list-inside space-y-1 mt-2">
                <li>Two novels of literary merit before half-term.</li>
                <li>Model UN participation continues weekly.</li>
              </ul>
            </div>
            <div className="edu360-teacher-panel">
              <div className="edu360-kicker mb-1">Long-Term Trajectory</div>
              <ul className="text-sm text-slate-700 list-disc list-inside space-y-1 mt-2">
                <li>On track for IGCSE profile with strong humanities pathway.</li>
                <li>Consider Duke of Edinburgh Bronze in Year 11.</li>
              </ul>
            </div>
          </div>
        </Section>

        <Section title="Principal’s Section" kicker="Section 13">
          <div className="edu360-principal-panel">
            <div className="flex items-start gap-6">
              <div>
                <TenantCrest tenant={school} size="lg" testId="report-crest-principal" />
              </div>
              <div className="flex-1">
                <div className="edu360-kicker">Principal Remarks</div>
                <p className="text-[15px] text-slate-800 leading-relaxed mt-2 font-display italic">
                  “{data.principal.remarks}”
                </p>
                <div className="mt-6 border-t border-slate-200 pt-4 flex items-end justify-between">
                  <div>
                    <div className="font-display italic text-2xl text-[color:var(--hl-navy)]">
                      {signatureName(data.principal.name)}
                    </div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 mt-1">
                      {data.principal.name} — {data.principal.title}
                    </div>
                    <div className="text-[10px] text-slate-400 uppercase tracking-widest mt-0.5">
                      Approval date: {data.principal.approval_date}
                    </div>
                  </div>
                  <div className="edu360-stamp">
                    <div className="text-[9px] uppercase tracking-widest text-white/80">Highlands</div>
                    <div className="font-display font-bold text-white text-sm">Official Seal</div>
                    <div className="text-[9px] uppercase tracking-widest text-white/70 mt-0.5">Est. 1998</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="edu360-kicker mb-2">Principal Recommendations</div>
              <ul className="space-y-2">
                {data.principal.recommendations.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Section>

        <div className="edu360-report-footer text-center">
          <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-2">Confidential Document</div>
          <div className="text-xs text-slate-600">
            {data.meta.confidentiality}
          </div>
          <div className="mt-3 text-[10px] uppercase tracking-widest text-slate-400">
            {school.name} · {school.address} · {school.phone}
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-400">
            Report {data.meta.version} · {data.meta.verification_id} · Generated {data.meta.generated_at?.slice(0, 10)}
          </div>
          <div className="mt-4 flex items-center justify-center gap-3">
            <div className="bg-white p-1.5 border border-slate-200 rounded">
              <QRCode value={verificationURL} size={56} />
            </div>
            <div className="text-left text-[10px] uppercase tracking-widest text-slate-400">
              Scan to verify authenticity<br />via Education360™
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileRow({ label, value, accent }) {
  return (
    <div>
      <div className="edu360-kicker">{label}</div>
      <div className={`text-sm font-semibold mt-0.5 ${accent ? "text-[color:var(--hl-orange)] text-lg font-display" : "text-slate-800"}`}>{value}</div>
    </div>
  );
}

function ageFrom(dob) {
  if (!dob) return "—";
  const b = new Date(dob);
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
  return `${age} years`;
}

function signatureName(name) {
  // First name signature-style
  const first = (name || "").split(" ").slice(-2).join(" ").replace(/[.,]/g, "");
  return first;
}
